import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import AnalyticsAPI from '../../../../APIs/analyticsAPI';
import API from '../../../../context/API';
import { Item, KeyMetricsFilter } from '../constants';

// Request parameters
export interface ItemSalesRequestParams {
  startDate?: string;
  endDate?: string;
  filter?: string;
  merchantId?: string;
  store?: string;
}

interface ItemSalesState {
  items: Item[];
  keyMetrics: KeyMetricsFilter;
  loading: boolean;
  error: string | null;
}

const initialState: ItemSalesState = {
  items: [],
  keyMetrics: {
    totalRevenue: "0.00",
    averageRevenue: "0.00",
    bestCategoryByRevenue: "0.00",
    bestCategoryByUnitsSold: 0,
  },
  loading: false,
  error: null,
};

// Async thunk to fetch item sales data
export const fetchItemSales = createAsyncThunk<
  { items: Item[]; keyMetrics: KeyMetricsFilter },
  ItemSalesRequestParams,
  { rejectValue: string }
>('itemSales/fetchItemSales', async (params, { rejectWithValue }) => {
  try {
    const options: any = {};
    
    if (params.startDate && params.endDate) {
      options.startDate = params.startDate;
      options.endDate = params.endDate;
    }
    
    if (params.filter) {
      options.filter = params.filter;
    }
    
    if (params.merchantId) {
      options.merchantId = params.merchantId;
    }
    
    if (params.store) {
      options.store = params.store;
    }

    const response = await AnalyticsAPI.getItemSales(options);
    const itemsData: Item[] = response?.data?.items || response?.items || [];
    
    // Calculate key metrics
    const totalRevenue = itemsData.length > 0
      ? itemsData.reduce(
          (sum, item) => sum + (parseFloat(String(item.totalRevenue || 0)) || 0),
          0
        )
      : 0;
    
    const averageRevenue = itemsData.length > 0 ? totalRevenue / itemsData.length : 0;

    // Find best item by revenue
    const bestItemByRevenue = itemsData.length > 0
      ? itemsData.reduce((prev, current) =>
          parseFloat(String(current.totalRevenue || 0)) >
          parseFloat(String(prev.totalRevenue || 0))
            ? current
            : prev
        )
      : { item: "No Data", totalRevenue: 0 };

    // Find best item by units sold
    const bestItemByUnitsSold = itemsData.length > 0
      ? itemsData.reduce((prev, current) =>
          (current.totalTimesSold || 0) > (prev.totalTimesSold || 0)
            ? current
            : prev
        )
      : { item: "No Data", totalTimesSold: 0 };

    const keyMetrics: KeyMetricsFilter = {
      totalRevenue: totalRevenue.toFixed(2),
      averageRevenue: averageRevenue.toFixed(2),
      bestCategoryByRevenue: parseFloat(
        String(bestItemByRevenue.totalRevenue || 0)
      ).toFixed(2),
      bestCategoryByUnitsSold: bestItemByUnitsSold.totalTimesSold || 0,
    };

    return {
      items: itemsData,
      keyMetrics,
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch item sales data'
    );
  }
});

// Async thunk to fetch stores
export const fetchStores = createAsyncThunk<
  { id: string; name?: string }[],
  void,
  { rejectValue: string }
>('itemSales/fetchStores', async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await API.get('/stores', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const stores = response.data.data || [];
    return stores;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch stores'
    );
  }
});

const itemSalesSlice = createSlice({
  name: 'itemSales',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearItemSales: (state) => {
      state.items = [];
      state.keyMetrics = initialState.keyMetrics;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch item sales data
      .addCase(fetchItemSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItemSales.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.keyMetrics = action.payload.keyMetrics;
      })
      .addCase(fetchItemSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.items = [];
        state.keyMetrics = initialState.keyMetrics;
      });
  },
});

export const { clearError, clearItemSales } = itemSalesSlice.actions;
export default itemSalesSlice.reducer;
